/*
  The clean look on WebGL 2: a frame through the look's LUT. Shared by the player (LookCanvas, on
  screen) and the look renderer (render.html, which bakes the look into a download), so both run the
  same shader. `display` adds the viewer's display transform (Rec.709 gamma 2.4 to sRGB) for a screen;
  a file keeps Rec.709, as Film Emulation's own export does.
*/
import { LUT_SIZE } from './look'

const VERT = `#version 300 es
in vec2 p; out vec2 uv;
void main() { uv = vec2(p.x * .5 + .5, .5 - p.y * .5); gl_Position = vec4(p, 0., 1.); }`
const frag = (display: boolean) => `#version 300 es
precision highp float; precision highp sampler3D;
uniform sampler2D frame; uniform sampler3D lut; in vec2 uv; out vec4 o;
// The Film Emulation viewer's display transform: Rec.709 (gamma 2.4) to sRGB for the screen.
vec3 display(vec3 c){vec3 l=pow(clamp(c,0.,1.),vec3(2.4));return mix(1.055*pow(l,vec3(1./2.4))-.055,l*12.92,lessThanEqual(l,vec3(.0031308)));}
void main() {
  vec3 c = clamp(texture(frame, uv).rgb, 0., 1.);
  // Sample on lattice points: the outer cells' centres sit half a texel in from each edge.
  float n = ${LUT_SIZE}.;
  vec3 v = texture(lut, c * ((n - 1.) / n) + .5 / n).rgb;
  o = vec4(${display ? 'display(v)' : 'v'}, 1.);
}`

export type LookGl = { gl: WebGL2RenderingContext; prog: WebGLProgram; buf: WebGLBuffer; frame: WebGLTexture; lut: WebGLTexture; display: boolean }

export function setupLook(canvas: HTMLCanvasElement, display = true): LookGl | null {
  const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: false })
  if (!gl) return null
  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader')
    return s
  }
  const prog = gl.createProgram()!
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT))
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, frag(display)))
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) || 'link')
  gl.useProgram(prog)
  const buf = gl.createBuffer()!
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
  const loc = gl.getAttribLocation(prog, 'p')
  gl.enableVertexAttribArray(loc)
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
  const tex = (unit: number, target: number) => {
    const t = gl.createTexture()!
    gl.activeTexture(gl.TEXTURE0 + unit)
    gl.bindTexture(target, t)
    for (const [k, v] of [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR], [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE]]) gl.texParameteri(target, k, v)
    return t
  }
  const frame = tex(0, gl.TEXTURE_2D)
  const lut = tex(1, gl.TEXTURE_3D)
  gl.uniform1i(gl.getUniformLocation(prog, 'frame'), 0)
  gl.uniform1i(gl.getUniformLocation(prog, 'lut'), 1)
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
  return { gl, prog, buf, frame, lut, display }
}

/** Puts a LUT (RGB8, LUT_SIZE cubed) on the GPU. */
export function uploadLut(c: LookGl, bytes: Uint8Array) {
  const { gl } = c
  gl.activeTexture(gl.TEXTURE1)
  gl.bindTexture(gl.TEXTURE_3D, c.lut)
  gl.texImage3D(gl.TEXTURE_3D, 0, gl.RGB8, LUT_SIZE, LUT_SIZE, LUT_SIZE, 0, gl.RGB, gl.UNSIGNED_BYTE, bytes)
}

/** One frame through the clean look onto the canvas at w x h. */
export function drawClean(c: LookGl, source: TexImageSource, w: number, h: number) {
  const { gl } = c
  gl.useProgram(c.prog)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  gl.bindBuffer(gl.ARRAY_BUFFER, c.buf)
  gl.viewport(0, 0, w, h)
  gl.activeTexture(gl.TEXTURE1)
  gl.bindTexture(gl.TEXTURE_3D, c.lut)
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, c.frame)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, source)
  gl.generateMipmap(gl.TEXTURE_2D)
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
}
